import { motion } from 'framer-motion';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ContactForm from '../components/Contact/ContactForm';

export default function ContactPage() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t('contactPage.pageTitle');
  }, [t]);

  return (
    <main id="main-content" className="min-h-screen">

      {/* Hero */}
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(168,67,151,0.06),transparent_60%)] pointer-events-none" />
        <div className="mx-auto min-w-0 max-w-7xl px-4 sm:px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-purple-primary text-xs font-bold tracking-widest uppercase font-label">{t('contactPage.badge')}</span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-black font-headline tracking-tight mt-4">
              {t('contactPage.heroTitle')} <span className="gradient-text">{t('contactPage.heroTitleHighlight')}</span>
            </h1>
            <p className="text-on-surface-variant text-lg mt-4 max-w-xl mx-auto">
              {t('contactPage.heroSubtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-24">
        <div className="mx-auto min-w-0 max-w-7xl px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">

            {/* Left — Info Panel (2 cols) */}
            <motion.div
              className="lg:col-span-2 space-y-8"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {/* Respuesta y soporte */}
              <div className="p-6 bg-[#1a1a1a] border border-outline-variant/30 rounded-xl">
                {[
                  { icon: 'schedule', title: t('contactPage.infoPanel.response.title'), desc: t('contactPage.infoPanel.response.desc') },
                  { icon: 'support_agent', title: t('contactPage.infoPanel.concierge.title'), desc: t('contactPage.infoPanel.concierge.desc') },
                  { icon: 'handshake', title: t('contactPage.infoPanel.dedicated.title'), desc: t('contactPage.infoPanel.dedicated.desc') },
                ].map((item) => (
                  <div key={item.icon} className="flex items-center gap-3 mb-4 last:mb-0">
                    <div className="w-10 h-10 rounded-lg bg-purple-primary/10 border border-purple-primary/30 flex items-center justify-center shrink-0">
                      <span className="material-symbols-outlined text-purple-primary text-[20px]">{item.icon}</span>
                    </div>
                    <div>
                      <h3 className="text-sm font-bold font-headline">{item.title}</h3>
                      <p className="text-xs text-on-surface-variant">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Badges de seguridad */}
              <div className="p-6 bg-[#1a1a1a] border border-outline-variant/30 rounded-xl">
                <h3 className="text-sm font-bold font-headline mb-4 uppercase tracking-wider text-on-surface-variant/70">{t('contactPage.infoPanel.security.title')}</h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { icon: 'security', label: t('contactPage.infoPanel.security.badges.pci') },
                    { icon: 'policy', label: t('contactPage.infoPanel.security.badges.aml') },
                    { icon: 'encrypted', label: t('contactPage.infoPanel.security.badges.ssl') },
                    { icon: 'verified_user', label: t('contactPage.infoPanel.security.badges.ofac') },
                  ].map((b) => (
                    <div key={b.label} className="flex items-center gap-2 text-on-surface-variant/60">
                      <span className="material-symbols-outlined text-purple-primary/50 text-[16px]">{b.icon}</span>
                      <span className="text-xs font-label">{b.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contacto directo */}
              <div className="p-6 bg-[#1a1a1a] border border-outline-variant/30 rounded-xl">
                <h3 className="text-sm font-bold font-headline mb-3 uppercase tracking-wider text-on-surface-variant/70">{t('contactPage.infoPanel.directContact.title')}</h3>
                <div className="space-y-2">
                  <a href={`mailto:${t('contactPage.infoPanel.directContact.email')}`} className="flex items-center gap-2 text-sm text-on-surface-variant hover:text-accent-magenta transition-colors">
                    <span className="material-symbols-outlined text-[16px]">mail</span>
                    {t('contactPage.infoPanel.directContact.email')}
                  </a>
                  <a href="https://wa.me/573114455762" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-on-surface-variant hover:text-accent-magenta transition-colors">
                    <span className="material-symbols-outlined text-[16px]">phone</span>
                    {t('contactPage.infoPanel.directContact.phone')}
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Right — Formulario (3 cols) */}
            <motion.div
              className="lg:col-span-3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="p-8 md:p-10 bg-[#1a1a1a] border border-outline-variant/30 rounded-2xl">
                <h2 className="text-2xl font-bold font-headline mb-1">{t('contactPage.form.title')}</h2>
                <p className="text-sm text-on-surface-variant mb-6">{t('contactPage.form.subtitle')}</p>

                <ContactForm
                  showTypeSelector
                  className="space-y-5"
                />

                <p className="text-[11px] text-on-surface-variant/40 text-center mt-4">
                  {t('contactPage.form.securityNote')}
                </p>
              </div>
            </motion.div>

          </div>
        </div>
      </section>
    </main>
  );
}
